import React, { useContext } from 'react';
import { Form, FormikProps } from 'formik';
import {
    ResponseError,
    SubmitButton,
    CheckboxField,
    DateField,
    TextAreaField,
    AsyncSelectField,
} from '@nintendo/forms';
import moment from 'moment-timezone';
import Button from 'react-bootstrap/Button';
import { FormType } from '../../../common/types/formTypes';
import { DATE_ONLY_FORMAT, SERVER_TIMEZONE } from '../../../../utils/Constants';
import { getOrganizations } from '../../../../api/amOrganizationApi';
import { getAmUsers } from '../../../../api/amUserApi';
import ConfigContext from '../../../../contexts/ConfigContext';
import { AmAdminItemType } from '../../../../api/amAdminApiTypes';
import { convertEmptyStringToDashes, noOptionsHandler } from '../../../../utils/displayUtils';

export interface UserAuthorizationFormType extends AmAdminItemType {
    id?: number;
    personId: number;
    personName: string;
    organizationId: number;
    organizationName: string;
}

export interface UserAuthorizationFormProps extends FormikProps<UserAuthorizationFormType> {
    formType: FormType;
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

const UserAuthorizationForm = (props: UserAuthorizationFormProps) => {
    const config = useContext(ConfigContext);

    const { values, errors, touched, isSubmitting, setFieldValue } = props;

    return (
        <div className="modal-content" data-testid="account-modal-content">
            <div className="modal-header">
                <h4 className="modal-title">
                    {props.formType === FormType.UPDATE ? 'Edit' : 'Create'} View Authorization
                </h4>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true" onClick={props.toggleModal}>
                        &times;
                    </span>
                </button>
            </div>

            <Form className="form-horizontal">
                <div className="modal-body">
                    <ResponseError errorMessage={props.responseError} />

                    <CheckboxField
                        label="Status"
                        name="active"
                        touched={touched.active}
                        errors={errors.active}
                        checkboxLabel="Active"
                        testId="active"
                    />
                    {props.formType === FormType.CREATE ? (
                        <AsyncSelectField
                            label="Person"
                            name="personId"
                            touched={touched.personId}
                            errors={errors.personId}
                            isRequired
                            asyncSelectProps={{
                                loadOptions: async (inputValue: string) => {
                                    if (inputValue.length > 2) {
                                        const users = await getAmUsers(config, {
                                            name: inputValue,
                                        });
                                        if (users) {
                                            return users.map((user) => ({
                                                value: user.personId.toString(),
                                                label: `${user.preferredName} (${user.title})`,
                                            }));
                                        }
                                    }
                                    return [];
                                },
                                noOptionsMessage: noOptionsHandler,
                            }}
                            onChange={(value) => {
                                if (value) {
                                    setFieldValue('personName', value.label);
                                } else {
                                    setFieldValue('personName', '');
                                }
                            }}
                            testId="personId"
                        />
                    ) : (
                        <div className="form-group row">
                            <div className="col-sm-5 control-label">Person</div>
                            <div className="col-sm-7">{props.values.personName}</div>
                        </div>
                    )}
                    {props.formType === FormType.CREATE || props.formType === FormType.CLONE ? (
                        <AsyncSelectField
                            asyncSelectProps={{
                                cacheOptions: true,
                                loadOptions: async (inputValue: string) => {
                                    if (inputValue.length > 2) {
                                        const organizations = await getOrganizations(
                                            config,
                                            inputValue,
                                        );
                                        if (organizations) {
                                            return organizations.map((organization) => ({
                                                value: organization.id.toString(),
                                                label: organization.name,
                                            }));
                                        }
                                    }
                                    return [];
                                },
                                placeholder: values.organizationName,
                                noOptionsMessage: noOptionsHandler,
                            }}
                            label="Organization"
                            name="organizationId"
                            touched={touched.organizationId}
                            errors={errors.organizationId}
                            testId="organizationId"
                            isRequired
                            onChange={(value) => {
                                if (value) {
                                    setFieldValue('organizationName', value.label);
                                } else {
                                    setFieldValue('organizationName', '');
                                }
                            }}
                        />
                    ) : (
                        <div className="form-group row">
                            <div className="col-sm-5 control-label">Organization</div>
                            <div className="col-sm-7">
                                {convertEmptyStringToDashes(props.values.organizationName || '')}
                            </div>
                        </div>
                    )}
                    <TextAreaField
                        label="Description"
                        name="description"
                        touched={touched.description}
                        errors={errors.description}
                        testId="description"
                    />
                    <DateField
                        label="Start Date"
                        name="startDate"
                        flatpickrProps={{
                            options: {
                                altInputClass: 'form-control',
                                altFormat: DATE_ONLY_FORMAT,
                                enableTime: false,
                                dateFormat: DATE_ONLY_FORMAT,
                                static: true,
                            },

                            value: values.startDate
                                ? moment(values.startDate).format(DATE_ONLY_FORMAT)
                                : undefined,
                        }}
                        timezone={SERVER_TIMEZONE}
                        touched={touched.startDate}
                        errors={errors.startDate}
                        isRequired
                        testId="startDate"
                    />
                    <DateField
                        label="End Date"
                        name="endDate"
                        flatpickrProps={{
                            options: {
                                altInputClass: 'form-control',
                                altFormat: DATE_ONLY_FORMAT,
                                enableTime: false,
                                dateFormat: DATE_ONLY_FORMAT,
                                static: true,
                            },
                            value: values.endDate
                                ? moment(values.endDate).format(DATE_ONLY_FORMAT)
                                : undefined,
                        }}
                        timezone={SERVER_TIMEZONE}
                        touched={touched.endDate}
                        errors={errors.endDate}
                        testId="endDate"
                    />
                </div>

                {/* Footer */}

                <div className="modal-footer">
                    <Button
                        variant="button"
                        className="btn-outline-secondary mr-auto"
                        onClick={props.toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        isSubmitting={isSubmitting}
                        disabled={!props.dirty}
                        buttonText="Save"
                    />
                </div>
            </Form>
        </div>
    );
};

export default UserAuthorizationForm;
